import Stats from 'stats.js'
import * as Tone from 'tone'

import GLScene from './GLScene'

import emitter from '@utils/emitter'

import pastNotes from './past-notes'

const IS_DEV = window.location.hash.includes('devmode')
const ASSETS_IMAGES = []

export default class GLManager {
  constructor ({ canvas }) {
    // DEV
    if (IS_DEV) {
      this.stats = new Stats()
      document.body.appendChild(this.stats.dom)
    }

    // CONFIG
    this.canvas = canvas
    this.isRendering = false
    this.isDesktop = window.innerWidth >= 1024
    window.isDesktop = this.isDesktop

    // CONTENT
    this.scene = new GLScene({ canvas, isDesktop: this.isDesktop })
    this.loadAssets()

    // EVENTS
    this.addEventListeners()

    // RENDERING
    this.update()
  }

  destroy () {
    this.stopUpdate()
    this.removeEventListeners()
  }

  async loadAssets () {
    const images = await Promise.all(
      ASSETS_IMAGES.map(
        (imageSrc) =>
          new Promise((resolve) => {
            const image = new Image()
            image.src = imageSrc
            image.addEventListener('load', () => {
              resolve(image)
            })
          }),
      ),
    )

    this.scene.handleAssetsLoad([...images])

    this.isRendering = true
  }

  createSound () {
    // BG NOISE
    const bassAutoFilter = new Tone.AutoFilter({ frequency: '1m', depth: 0.05 }).start()
    const highAutoFilter = new Tone.AutoFilter({ frequency: '2n', depth: 0.075 }).start()

    this.bassNoise = new Tone.Noise({ type: 'brown', playbackRate: 0.2, volume: -20, fadeIn: 4 })

    this.bassNoise.chain(highAutoFilter, bassAutoFilter, Tone.Destination)

    setTimeout(() => {
      this.bassNoise.start()
    }, 500)
  }

  createSynth () {
    const reverbFilter = new Tone.Reverb({ decay: 8, wet: 1 })
    const distFilter = new Tone.Distortion(0.05).toDestination()

    this.synth = new Tone.DuoSynth(Tone.Synth, {
      portamento: 0.1,
      oscillator: {
        partials: [0, 2, 3, 4],
        envelope: {
          attack: 0.1,
          decay: 0.3,
          sustain: 1,
          release: 2,
        },
      },
    })
    // this.synth.set({ detune: -1200 })
    // this.synth = new Tone.Synth({
    //   volume: -Infinity,
    //   envelope: {
    //     attack: 0.2,
    //     decay: 0.3,
    //     sustain: 1,
    //     release: 1,
    //   },
    // })
    // this.synth.oscillator.set({ frequency: 'C2', type: 'fattriangle', count: 2 })
    this.synth.chain(reverbFilter, distFilter, Tone.Destination)
  }

  // EVENTS HANDLING
  handleResize () {
    this.scene.handleResize()
  }

  handleMouseMove (e) {
    if (this.scene) this.scene.handleMouseMove(e)
  }

  handleMouseDown (e) {
    if (this.scene) this.scene.handleMouseDown(e)
  }

  handleMouseUp (e) {
    if (this.scene) this.scene.handleMouseUp(e)
  }

  handleKeyDown (e) {
    if (this.scene) this.scene.handleKeyDown(e)
  }

  handleKeyUp (e) {
    if (this.scene) this.scene.handleKeyUp(e)
  }

  // RENDERING
  update (t) {
    if (this.isRendering) {
      if (IS_DEV) this.stats.begin()

      this.scene.update(t)

      if (IS_DEV) this.stats.end()
    }
  }

  addEventListeners () {
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true })
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this))
    this.resizeObserver.observe(document.body)

    if (this.isDesktop) {
      window.addEventListener('mousemove', this.handleMouseMove.bind(this), { passive: true })
      this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this), { passive: true })
      this.canvas.addEventListener('mouseup', this.handleMouseUp.bind(this), { passive: true })
      window.addEventListener('keydown', this.handleKeyDown.bind(this), { passive: true })
      window.addEventListener('keyup', this.handleKeyUp.bind(this), { passive: true })
    } else {
      this.canvas.addEventListener('touchend', this.handleMouseUp.bind(this), { passive: true })
    }

    emitter.on('LOADING_HIDE', () => {
      this.scene.handleLoadingHide()
      this.createSound()
    })

    emitter.on('AUDIO_ON', () => {
      Tone.getDestination().volume.rampTo(0, 2)
    })

    emitter.on('AUDIO_OFF', () => {
      Tone.getDestination().volume.rampTo(-Infinity, 2)
    })

    emitter.on('AUDIO_SYNTH_ON', () => {
      this.createSynth()

      this.synthNoteCurrent = 0
      this.bassNoise.volume.rampTo(-30, 2)
      this.synth.volume.rampTo(-10, 0.5)

      const now = Tone.now() + 1
      pastNotes.forEach((note) => {
        this.synth.triggerAttackRelease(note.name, note.duration * 5, note.time * 5 + now, note.velocity)
      })
    })

    emitter.on('AUDIO_SYNTH_OFF', () => {
      this.bassNoise.volume.rampTo(-20, 2)
      this.synth.volume.rampTo(-Infinity, 2)
      this.synth.dispose()
    })
  }

  removeEventListeners () {
    window.removeEventListener('resize', this.handleResize)
    this.resizeObserver.disconnect()

    if (this.isDesktop) {
      window.removeEventListener('mousemove', this.handleMouseMove)
      this.canvas.removeEventListener('mousedown', this.handleMouseDown)
      this.canvas.removeEventListener('mouseup', this.handleMouseUp)
      window.removeEventListener('keydown', this.handleKeyDown)
      window.removeEventListener('keyup', this.handleKeyUp)
    }
  }
}
